import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'src/components';
import { humanizeConversionName } from 'src/utils/conversionLabels';
import messages from '../messages';
import styles from './ReplaceContentDialog.module.scss';

type ReplaceContentDialogProps = {
  /** Every stored format the replace deletes (`storedConversions`). */
  formats: string[];
  /** The subset that will not be recreated (`conversionsLostOnReplace`). */
  notRecreated: string[];
  onReplace: () => void;
  onUploadAsNew: () => void;
  onCancel: () => void;
};

/**
 * Shown before Replace Content whenever the asset has stored formats (#958):
 * the replace deletes all of them. Names each one, marks those that will not
 * come back, and offers the non-destructive route (a new asset).
 */
export const ReplaceContentDialog: React.FC<ReplaceContentDialogProps> = ({
  formats,
  notRecreated,
  onReplace,
  onUploadAsNew,
  onCancel,
}) => {
  const lost = new Set(notRecreated);
  return (
    <div className={styles.overlay} data-test="replace-content-dialog">
      <div className={styles.backdrop} onClick={onCancel} />
      <div className={styles.dialog} role="alertdialog" aria-labelledby="replace-content-title">
        <h3 id="replace-content-title">
          <FormattedMessage {...messages.replaceConfirmTitle} />
        </h3>
        <p>
          <FormattedMessage {...messages.replaceDeletesFormats} values={{ count: formats.length }} />
        </p>
        {notRecreated.length > 0 && (
          <p className={styles.warning}>
            <FormattedMessage {...messages.replaceNotRecreatedNote} values={{ count: notRecreated.length }} />
          </p>
        )}
        <ul className={styles.list}>
          {formats.map((name) => (
            <li key={name} data-test="replace-content-format">
              {humanizeConversionName(name)}
              {humanizeConversionName(name) !== name && <code>{name}</code>}
              {lost.has(name) && (
                <span className={styles.badge}>
                  <FormattedMessage {...messages.replaceNotRecreatedBadge} />
                </span>
              )}
            </li>
          ))}
        </ul>
        <p className={styles.hint}>
          <FormattedMessage {...messages.replaceKeepFormatsHint} />
        </p>
        <div className={styles.controls}>
          <Button mode="flat" size="small" onClick={onCancel} datatest="replace-content-cancel">
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button mode="secondary" size="small" onClick={onUploadAsNew} datatest="replace-content-upload-new">
            <FormattedMessage {...messages.replaceUploadAsNew} />
          </Button>
          <Button mode="decline" size="small" onClick={onReplace} datatest="replace-content-confirm">
            <FormattedMessage {...messages.replaceAnyway} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReplaceContentDialog;
